import { FC } from "preact/compat";
import { useEffect, useRef } from "preact/hooks";
import { useSignal } from "@preact/signals";
import { Slot } from "@radix-ui/react-slot";
import { useApp } from "~/core";
import { HelpTag } from "~/core/slices/help/tags";
import DiscoveryItemContent from "./DiscoveryItemContent";
import DiscoveryItemPopover from "./DiscoveryItemPopover";
import useItemSetup from "./useItemSetup";

const DiscoveryGlobalFallback: FC = () => {
    const { help } = useApp();
    const ref = useRef<HTMLDivElement>(null);
    const caughtTag = useSignal("");
    const isOpened = useSignal(false);
    const helpHistory = help.history.value;
    const isInHistory = Boolean(
        helpHistory.find((i) => i.tag === caughtTag.value),
    );
    const isActiveRecord =
        help.activeHistoryRecord.value?.tag === caughtTag.value &&
        isOpened.value;

    useEffect(() => {
        const handleOpenDiscovery = (event: Event) => {
            if (
                !(event instanceof CustomEvent) ||
                typeof event.detail.tag !== "string" ||
                event.detail.force !== true
            ) {
                return;
            }

            caughtTag.value = event.detail.tag;
            isOpened.value = true;

            window.dispatchEvent(
                new CustomEvent("discovery.item_opened", {
                    detail: { tag: event.detail.tag, id: event.detail.tag },
                }),
            );
        };

        const handleCloseHistory = () => {
            isOpened.value = false;
        };

        const handleItemOpened = (event: Event) => {
            if (
                !isOpened.peek() ||
                !(event instanceof CustomEvent) ||
                typeof event.detail.tag !== "string" ||
                caughtTag.peek() === event.detail.tag
            ) {
                return;
            }

            isOpened.value = false;
        };

        window.addEventListener(
            "discovery.open_discovery",
            handleOpenDiscovery,
        );
        window.addEventListener("discovery.close_history", handleCloseHistory);
        window.addEventListener("discovery.item_opened", handleItemOpened);

        return () => {
            window.removeEventListener(
                "discovery.open_discovery",
                handleOpenDiscovery,
            );
            window.removeEventListener(
                "discovery.close_history",
                handleCloseHistory,
            );
            window.removeEventListener(
                "discovery.item_opened",
                handleItemOpened,
            );
        };
    }, [caughtTag, isOpened]);

    useItemSetup({
        ref,
        isActiveRecord,
        isInHistory,
    });

    return (
        <div ref={ref} className="absolute">
            <DiscoveryItemPopover
                tag={caughtTag.value as HelpTag}
                open={isActiveRecord}
                buttonRect={{ top: 62, left: 62, width: 420, height: 540 }}
                maxWidth={420}
                maxHeight={540}
                zIndex={101}
                position="fixed"
                controlled
            >
                <Slot>
                    <DiscoveryItemContent
                        tag={caughtTag.value as HelpTag}
                        onClose={() => (isOpened.value = false)}
                        classNames={{
                            adminEdit: "-mx-4 -mb-4 mt-4 bg-gray-100 py-2 px-3",
                        }}
                    />
                </Slot>
            </DiscoveryItemPopover>
        </div>
    );
};

const Wrapper: FC = () => {
    const { help } = useApp();

    return help.isDiscovery.value ? <DiscoveryGlobalFallback /> : null;
};

export default Wrapper;
